import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('habit2goals_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Habits API
export const habitsAPI = {
  create: async (habitData) => {
    const response = await api.post('/habits', habitData);
    return response.data;
  },
  
  getAll: async () => {
    const response = await api.get('/habits');
    return response.data;
  },
  
  update: async (habitId, habitData) => {
    const response = await api.put(`/habits/${habitId}`, habitData);
    return response.data;
  },
  
  delete: async (habitId) => {
    const response = await api.delete(`/habits/${habitId}`);
    return response.data;
  },
  
  reorder: async (habitIds) => {
    const response = await api.post('/habits/reorder', { habitIds });
    return response.data;
  },
};

// Logs API
export const logsAPI = {
  toggle: async (habitId, date, completed, notes = null) => {
    const response = await api.post('/logs', { habitId, date, completed, notes });
    return response.data;
  },
  
  getAll: async () => {
    const response = await api.get('/logs');
    return response.data;
  },
  
  getByHabit: async (habitId) => {
    const response = await api.get(`/logs/${habitId}`);
    return response.data;
  },
  
  updateNotes: async (habitId, date, notes) => {
    const response = await api.put(`/logs/${habitId}/${date}/notes`, null, { params: { notes } });
    return response.data;
  },

  getJournal: async (limit = 50, offset = 0) => {
    const response = await api.get('/logs/journal', { params: { limit, offset } });
    return response.data;
  },
};

// Profile API
export const profileAPI = {
  get: async () => {
    const response = await api.get('/profile');
    return response.data;
  },
  
  update: async (profileData) => {
    const response = await api.put('/profile', profileData);
    return response.data;
  },
  
  getAvatars: async () => {
    const response = await api.get('/profile/avatars');
    return response.data;
  },
};

// Quotes API
export const quotesAPI = {
  getDaily: async () => {
    const response = await api.get('/quotes/daily');
    return response.data;
  },
};

// Progress API
export const progressAPI = {
  getMilestones: async () => {
    const response = await api.get('/progress/milestones');
    return response.data;
  },
};

// Subscription API
export const subscriptionAPI = {
  getStatus: async () => {
    const response = await api.get('/subscription/status');
    return response.data;
  },
  
  getPlans: async () => {
    const response = await api.get('/subscription/plans');
    return response.data;
  },
  
  startTrial: async () => {
    const response = await api.post('/subscription/start-trial');
    return response.data;
  },
  
  createCheckout: async (planId) => {
    const originUrl = window.location.origin;
    const response = await api.post('/subscription/checkout', { 
      plan_id: planId,
      origin_url: originUrl 
    });
    return response.data;
  },
  
  checkPaymentStatus: async (sessionId) => {
    const response = await api.get(`/subscription/checkout/status/${sessionId}`);
    return response.data;
  },
  
  cancelSubscription: async () => {
    const response = await api.post('/subscription/cancel');
    return response.data;
  },
};

// Export API
export const exportAPI = {
  downloadCSV: async () => {
    const token = localStorage.getItem('habit2goals_token');
    const response = await fetch(`${API_URL}/export/csv`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    return blob;
  },
};

// Share API
export const shareAPI = {
  createShareLink: async () => {
    const response = await api.post('/share/create');
    return response.data;
  },
  
  getSharedProgress: async (token) => {
    const response = await api.get(`/share/${token}`);
    return response.data;
  },
};

export default api;
